import React, { useCallback, useContext, useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import Group from 'components/utils/Group'
import InclusionsSection from '../FlashHotel/InclusionsSection'
import TextLink from 'LuxKit/TextLink'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { take } from 'lib/array/arrayUtils'
import moment from 'moment'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import ModalContext from 'contexts/ModalContext'
import Modal from 'LuxKit/Modal/Modal'

interface MappedProps {
  checkIn?: string
  duration?: number
  inclusionsViewableLuxPlusTier: App.MembershipSubscriptionTier
}

interface Props extends MappedProps {
  offer: App.Offer
  packageOption: App.OfferPackageOptionView
}

const NUM_INCLUSIONS_TO_SHOW = 3

function MultipleRoomsBookingOption(props: Props) {
  const {
    offer,
    checkIn,
    duration,
    packageOption,
  } = props

  const pkg = packageOption.package
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])
  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const totalInclusionsCount = inclusions.length + bonusInclusions.length

  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={pkg.name}
      primaryActionText="Close"
    >
      <Group direction="vertical" gap={12}>
        {!!luxPlusInclusions?.length && <LuxPlusInclusions
          luxPlusInclusions={luxPlusInclusions}
          accommodationOffer={offer}
        />}
        <InclusionsSection
          inclusions={inclusions}
          bonusInclusions={bonusInclusions}
        />
      </Group>
    </Modal>)
  }, [showModal, pkg.name, luxPlusInclusions, offer, inclusions, bonusInclusions])

  return (
    <Group direction="vertical" gap={8}>
      {!!luxPlusInclusions?.length && <LuxPlusInclusions
        luxPlusInclusions={luxPlusInclusions}
        accommodationOffer={offer}
      />}
      <InclusionsSection
        inclusions={take(inclusions, NUM_INCLUSIONS_TO_SHOW)}
        bonusInclusions={bonusInclusions}
      />
      {totalInclusionsCount > NUM_INCLUSIONS_TO_SHOW &&
        <TextLink size="medium" weight="bold" onClick={openAllInclusionsModal}>
          {`+ ${totalInclusionsCount - NUM_INCLUSIONS_TO_SHOW} more inclusions`}
        </TextLink>}
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkIn: state.checkInDate,
  duration: state.duration,
  inclusionsViewableLuxPlusTier: getInclusionsViewableLuxPlusTier(appState),
}), MultipleRoomsBookingOption)
